import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  archiveLessons,
  browseLessons,
  relabelLessons,
} from '@platform/client';
import { readinessSchema } from '@platform/schema/authoring';
import {
  contentStatusSchema,
  difficultySchema,
  reviewStatusSchema,
} from '@platform/schema/content';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { personaName, PERSONAS } from '../authoring/personas';
import { PublishDialog, RowPublishCell } from '../authoring/PublishDialog';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { helpfulShown, reasonsShown, shownDate } from './feedback';
import { FilterSelect, sameAs } from './FilterSelect';
import { LessonsChat } from './LessonsChat';
import { PAGE, Pager } from './Pager';
import { PlacePicker } from './PlacePicker';
import {
  NO_PLACES,
  pickCourse,
  pickLesson,
  pickModule,
  placeQuery,
  UNPLACED,
} from './places';
import { LevelSelect, PersonaSelect } from './Relabel';
import { courseSearch, lessonSearch, moduleSearch } from './sources';
import { REVIEW_SHOWN, STATUS_COLOUR } from './status';
import { SUBJECT_OPTIONS } from './subject';

import type { Places } from './places';
import type { PublishTarget } from '../authoring/PublishDialog';
import type { BrowseLessonsResponse } from '@platform/client';
import type { LessonSort } from '@platform/schema/authoring';
import type { Difficulty, Persona } from '@platform/schema/content';

/**
 * Lessons — GEN-61, and the one library screen that is not a `Library` binding.
 *
 * Why it is not, and what it keeps of the pattern anyway:
 * `docs/decisions/library-screens.md`. A lesson opens into `ReviewWorkspace`, which stays
 * the editing surface; this is the way in, which is what the console was missing.
 */

/** Where a long title or place name stops and the tooltip takes over — ADMIN-17. */
const TITLE_WIDTH = 220;
/** A title wraps to two lines before it is cut; the rest of a row cuts at one. */
const TWO_LINES = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;
const PLACE_WIDTH = 140;

const NO_FILTERS = {
  q: '',
  subject: '',
  difficulty: '',
  status: '',
  review: '',
  readiness: '',
  persona: '',
  tag: '',
  feedbackDays: '',
};

type Filters = typeof NO_FILTERS;

const SORTS: { value: LessonSort; label: string }[] = [
  { value: 'CREATED_DESC', label: 'Last created — newest first' },
  { value: 'CREATED_ASC', label: 'Last created — oldest first' },
  { value: 'PUBLISHED_DESC', label: 'Last published — newest first' },
  { value: 'TITLE_ASC', label: 'Lesson title — A–Z' },
  { value: 'TITLE_DESC', label: 'Lesson title — Z–A' },
  { value: 'UPDATED_DESC', label: 'Last updated — newest first' },
  { value: 'HELPFUL_ASC', label: 'Helpful rate — lowest first' },
  { value: 'HELPFUL_DESC', label: 'Helpful rate — highest first' },
  { value: 'NOT_HELPFUL_DESC', label: 'Most “Not really” answers' },
  { value: 'FEEDBACK_DESC', label: 'Most feedback received' },
  { value: 'FEEDBACK_RECENT', label: 'Last feedback received' },
];

/** The feedback date window, in days back from now. */
const WINDOWS = [
  { value: '1', label: 'Last day' },
  { value: '5', label: 'Last 5 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
];

type Row = BrowseLessonsResponse['lessons'][number];

interface DateShown {
  heading: string;
  of: (row: Row) => string | undefined;
}

const CREATED: DateShown = { heading: 'Created', of: (row) => row.createdAt };

/** The date column shows what the list is sorted by, and when it was made otherwise. */
const DATE_SHOWN: Record<LessonSort, DateShown> = {
  CREATED_DESC: CREATED,
  CREATED_ASC: CREATED,
  PUBLISHED_DESC: { heading: 'Published', of: (row) => row.publishedAt },
  TITLE_ASC: CREATED,
  TITLE_DESC: CREATED,
  UPDATED_DESC: { heading: 'Updated', of: (row) => row.updatedAt },
  HELPFUL_ASC: CREATED,
  HELPFUL_DESC: CREATED,
  NOT_HELPFUL_DESC: CREATED,
  FEEDBACK_DESC: CREATED,
  FEEDBACK_RECENT: CREATED,
};

export function Lessons() {
  const queries = useQueryClient();

  const [filters, setFilters] = useState(NO_FILTERS);
  const [places, setPlaces] = useState<Places>(NO_PLACES);
  const [sort, setSort] = useState<LessonSort>('CREATED_DESC');
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  const [asking, setAsking] = useState(false);
  const [publishing, setPublishing] = useState<PublishTarget | null>(null);

  /**
   * What the bulk persona picker is showing — GEN-70's adversary.
   *
   * Held rather than left empty. The write replaces the set, and a picker rendered from
   * `[]` reports only the option just clicked: a second persona replaced the first
   * instead of joining it, so two personas could not be set in bulk at all.
   */
  const [bulkPersonas, setBulkPersonas] = useState<Persona[]>([]);

  // The window is sent as a number of days; every other filter goes as it is set.
  const { feedbackDays, ...rest } = filters;
  const page = useQuery({
    queryKey: ['lessons', filters, places, sort, offset],
    queryFn: () =>
      browseLessons({
        query: {
          limit: PAGE,
          offset,
          sort,
          ...set(rest),
          ...placeQuery(places),
          ...(feedbackDays === ''
            ? {}
            : { feedbackDays: Number(feedbackDays) }),
        },
        throwOnError: true,
      }),
  });

  const archive = useTrackedMutation('Archive the selected lessons', {
    mutationFn: async (lessonIds: string[]) => {
      await archiveLessons({ body: { lessonIds }, throwOnError: true });
    },
    onSuccess: async () => {
      setPicked([]);
      await queries.invalidateQueries({ queryKey: ['lessons'] });
    },
  });

  /**
   * Set the level or the personas on one row, or on every row selected — GEN-70.
   *
   * One mutation for both, because it is one act: the row control sends the row's own id
   * and the bar sends the selection, and a second mutation would be a second way to write
   * the same two columns. The selection survives it — a reviewer setting a level on twenty
   * lessons usually sets the personas next.
   */
  const relabel = useTrackedMutation('Relabel the lessons', {
    mutationFn: async (change: {
      lessonIds: string[];
      difficulty?: Difficulty;
      personas?: Persona[];
    }) => {
      await relabelLessons({ body: change, throwOnError: true });
    },
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['lessons'] });
    },
  });

  const rows = page.data?.data.lessons ?? [];
  const total = page.data?.data.total ?? 0;

  // Every filter change starts a new list, so an offset from the old one lands somewhere
  // arbitrary in the new one — and a selection made in the old one is a set of rows the
  // reader can no longer see.
  const refine = (change: Partial<Filters>, order = sort): void => {
    setFilters({ ...filters, ...change });
    setSort(order);
    setOffset(0);
    setPicked([]);
    setBulkPersonas([]);
    setAsking(false);
  };

  const place = (next: Places): void => {
    setPlaces(next);
    refine({});
  };

  /** What the bulk controls announce they would change. */
  const chosen = `${String(picked.length)} lesson${picked.length === 1 ? '' : 's'}`;

  const toggle = (id: string): void => {
    setBulkPersonas([]);
    setPicked(
      picked.includes(id)
        ? picked.filter((one) => one !== id)
        : [...picked, id],
    );
  };

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader title="Lessons" />

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <PlacePicker
          label="Course"
          width={220}
          value={places.course}
          search={courseSearch}
          onChange={(course) => {
            place(pickCourse(places, course));
          }}
        />
        <PlacePicker
          label="Module"
          width={240}
          value={places.module}
          search={moduleSearch(places.course)}
          // A course holds only placed lessons, so under one there is nothing unplaced.
          lead={places.course === null ? UNPLACED : undefined}
          caption={(one) => one.course?.title}
          onChange={(module) => {
            place(pickModule(places, module));
          }}
        />
        <PlacePicker
          label="Lesson"
          width={260}
          value={places.lesson}
          search={lessonSearch(places)}
          caption={(one) => one.module?.title}
          onChange={(lesson) => {
            place(pickLesson(places, lesson));
          }}
        />
        <TextField
          size="small"
          label="Search title or slug"
          value={filters.q}
          onChange={(event) => {
            refine({ q: event.target.value });
          }}
          sx={{ minWidth: 240 }}
        />
        <FilterSelect
          label="Topic"
          value={filters.subject}
          width={170}
          options={SUBJECT_OPTIONS}
          onChange={(subject) => {
            refine({ subject });
          }}
        />
        <FilterSelect
          label="Level"
          value={filters.difficulty}
          width={150}
          options={sameAs(difficultySchema.options)}
          onChange={(difficulty) => {
            refine({ difficulty });
          }}
        />
        <FilterSelect
          label="Persona"
          value={filters.persona}
          width={190}
          options={PERSONAS.map((persona) => ({
            value: persona,
            label: personaName(persona),
          }))}
          onChange={(persona) => {
            refine({ persona });
          }}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          width={150}
          options={sameAs(contentStatusSchema.options)}
          onChange={(status) => {
            refine({ status });
          }}
        />
        <FilterSelect
          label="Review"
          value={filters.review}
          width={160}
          options={reviewStatusSchema.options.map((value) => ({
            value,
            label: REVIEW_SHOWN[value].label,
          }))}
          onChange={(review) => {
            refine({ review });
          }}
        />
        <FilterSelect
          label="Publish readiness"
          value={filters.readiness}
          width={170}
          options={sameAs(readinessSchema.options)}
          onChange={(readiness) => {
            refine({ readiness });
          }}
        />
        <FilterSelect
          label="Feedback in"
          value={filters.feedbackDays}
          width={150}
          options={WINDOWS}
          onChange={(feedbackDays) => {
            refine({ feedbackDays });
          }}
        />
        <TextField
          select
          size="small"
          label="Sort"
          value={sort}
          onChange={(event) => {
            refine({}, event.target.value as LessonSort);
          }}
          sx={{ minWidth: 250 }}
        >
          {SORTS.map((one) => (
            <MenuItem key={one.value} value={one.value}>
              {one.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Archive is archived rather than deleted, for the reason `CollectionHeader` gives
          one level up, and it stays the only bulk *status* change: a bulk publish would be
          the publish gate applied without anyone reading what it refused. Level and
          personas are safe in bulk because neither is a gate — GEN-70. */}
      {picked.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="body2">
            {String(picked.length)} selected
          </Typography>
          <LevelSelect
            label="Set level"
            describedAs={`Set the level on ${chosen}`}
            value=""
            disabled={relabel.isPending}
            onChange={(difficulty) => {
              relabel.mutate({ lessonIds: picked, difficulty });
            }}
          />
          <PersonaSelect
            label="Set personas"
            describedAs={`Set the personas on ${chosen}`}
            value={bulkPersonas}
            disabled={relabel.isPending}
            onChange={(personas) => {
              setBulkPersonas(personas);
              relabel.mutate({ lessonIds: picked, personas });
            }}
          />
          <Button
            size="small"
            onClick={() => {
              setAsking(!asking);
            }}
          >
            {asking ? 'Close the planner' : 'Ask the planner'}
          </Button>
          <Button
            size="small"
            color="error"
            disabled={archive.isPending}
            onClick={() => {
              archive.mutate(picked);
            }}
          >
            Archive
          </Button>
        </Stack>
      )}

      {/* Keyed on the selection, so changing which lessons are picked starts a fresh
          conversation rather than carrying a transcript about lessons that have left it.
          The panel holds the turns, and nothing on the server does — GEN-52. */}
      {asking && picked.length > 0 && (
        <LessonsChat key={picked.join(',')} lessonIds={picked} />
      )}

      {page.isPending && <LinearProgress />}

      {!page.isPending && rows.length === 0 && (
        <Alert severity="info">
          {filters.feedbackDays !== ''
            ? 'No lesson was answered in that window. Widen it to see more.'
            : Object.values(filters).some((one) => one !== '') ||
                Object.values(places).some((one) => one !== null)
              ? 'Nothing matches those filters. Clear one to see more.'
              : 'No lessons yet. Run a generation to make the first ones.'}
        </Alert>
      )}

      {rows.length > 0 && (
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Lesson</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Paths</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Review</TableCell>
              <TableCell>Publish</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>{DATE_SHOWN[sort].heading}</TableCell>
              <TableCell align="right">Helpful</TableCell>
              <TableCell align="right">Yes / Not really</TableCell>
              <TableCell align="right">Answers</TableCell>
              <TableCell>Top reason</TableCell>
              <TableCell>Last feedback</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Personas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={picked.includes(row.id)}
                    slotProps={{
                      input: { 'aria-label': `Select ${row.title}` },
                    }}
                    onChange={() => {
                      toggle(row.id);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Stack sx={{ maxWidth: TITLE_WIDTH }}>
                    <Tooltip title={row.title} describeChild>
                      <Typography variant="body2" sx={TWO_LINES}>
                        <Link to={`/authoring/lessons/${row.id}`}>
                          {row.title}
                        </Link>
                      </Typography>
                    </Tooltip>
                    <Tooltip title={row.slug} describeChild>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {row.slug}
                      </Typography>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <PlaceCell text={row.module?.title ?? UNPLACED.title} />
                <PlaceCell text={row.course?.title ?? '—'} />
                <PlaceCell
                  text={
                    row.paths.length === 0
                      ? '—'
                      : row.paths.map((one) => one.title).join(', ')
                  }
                />
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLOUR[row.status]}
                    label={row.status}
                  />
                </TableCell>
                <ReviewCell review={row.review} />
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.blockers === 0
                    ? 'Ready'
                    : `${String(row.blockers)} blocker(s)`}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                  >
                    {`${String(row.scenes)} scene${row.scenes === 1 ? '' : 's'}`}
                  </Typography>
                </TableCell>
                <RowPublishCell
                  status={row.status}
                  title={row.title}
                  onClick={(action) => {
                    setPublishing({
                      lesson: row.id,
                      action,
                      name: row.title,
                      place: row.module?.title ?? UNPLACED.title,
                    });
                  }}
                />
                <TableCell>{shownDate(DATE_SHOWN[sort].of(row))}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {helpfulShown(row.feedback)}
                </TableCell>
                <TableCell align="right">
                  {`${String(row.feedback.helpful)} / ${String(row.feedback.notHelpful)}`}
                </TableCell>
                <TableCell align="right">{row.feedback.count}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {reasonsShown(
                    row.feedback.topReason === undefined
                      ? []
                      : [row.feedback.topReason],
                  )}
                </TableCell>
                <TableCell>{shownDate(row.feedback.lastAt)}</TableCell>
                <TableCell>{`v${String(row.version)}`}</TableCell>
                <TableCell>
                  <LevelSelect
                    describedAs={`Level for ${row.title}`}
                    value={row.difficulty ?? ''}
                    disabled={relabel.isPending}
                    onChange={(difficulty) => {
                      relabel.mutate({ lessonIds: [row.id], difficulty });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <PersonaSelect
                    describedAs={`Personas for ${row.title}`}
                    value={row.personas}
                    disabled={relabel.isPending}
                    onChange={(personas) => {
                      relabel.mutate({ lessonIds: [row.id], personas });
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PublishDialog
        target={publishing}
        onClose={() => {
          setPublishing(null);
        }}
        onDone={() => queries.invalidateQueries({ queryKey: ['lessons'] })}
      />

      <Pager
        offset={offset}
        total={total}
        onOffset={(next) => {
          setOffset(next);
          setPicked([]);
        }}
      />
    </Stack>
  );
}

/** The filters that are actually set. An empty one is not a filter, it is the default. */
const set = (
  filters: Omit<Filters, 'feedbackDays'>,
): Partial<Omit<Filters, 'feedbackDays'>> =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== ''),
  );

/** A module, course or path list, cut to one line with the whole of it on hover. */
function PlaceCell({ text }: { text: string }) {
  return (
    <TableCell>
      <Tooltip title={text} describeChild>
        <Typography variant="body2" noWrap sx={{ maxWidth: PLACE_WIDTH }}>
          {text}
        </Typography>
      </Tooltip>
    </TableCell>
  );
}

/** The review chip, and for NEEDS_CHANGES the start of why, whole on hover. */
function ReviewCell({ review }: { review: Row['review'] }) {
  if (review === undefined) return <TableCell />;

  const shown = REVIEW_SHOWN[review.status];

  return (
    <TableCell>
      <Chip size="small" color={shown.colour} label={shown.label} />
      {review.reason !== undefined && (
        <Tooltip title={review.reason} describeChild>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            noWrap
            sx={{ maxWidth: PLACE_WIDTH }}
          >
            {review.reason}
          </Typography>
        </Tooltip>
      )}
    </TableCell>
  );
}
